import { useCallback, useEffect, useState } from "react";
import { Loader2, Globe, AlertCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { api, get } from "@/lib/api";
import { SettingsSection } from "@/components/settings/SettingsSection";
import { cn } from "@/lib/utils";

interface AudienceProfile {
  who: string;
  where: string;
  context: string;
}

interface Settings {
  enabled: boolean;
  webSearchEnabled: boolean;
  monthlyCapUsd: number | null;
  audience: AudienceProfile;
}

/** Matches MAX_AUDIENCE_FIELD_CHARS on the server. */
const AUDIENCE_MAX = 600;

const AUDIENCE_FIELDS: {
  key: keyof AudienceProfile;
  label: string;
  placeholder: string;
  hint: string;
}[] = [
  {
    key: "who",
    label: "Who they are",
    placeholder:
      "Around 80 on a Sunday. Mostly retired, a handful of young families. Long memories, not much churchgoing background in the under-40s.",
    hint: "The room as it actually is, not as you'd describe it to a bishop.",
  },
  {
    key: "where",
    label: "Where they are",
    placeholder: "Post-industrial town in the north east. Work is scarce and people stay.",
    hint: "Place shapes what an illustration has to do before it lands.",
  },
  {
    key: "context",
    label: "Worth holding in mind",
    placeholder:
      "We're partway through Lent. A long-standing member died last month and it's still raw.",
    hint: "Season, recent events, anything tender. Come back and clear it when it passes.",
  },
];

interface Usage {
  today: number;
  month: number;
  allTime: number;
  byFeature: { feature: string; costUsd: number; calls: number }[];
  recent: {
    feature: string;
    model: string;
    costUsd: number;
    searchCount: number;
    createdAt: string;
  }[];
}

const money = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;

const FEATURE_LABELS: Record<string, string> = {
  suggestions: "Tags & main point",
  "research:grounded": "Background research",
  "research:prompts": "Questions",
};

export function AISettings() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [capInput, setCapInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  /** Local draft: the audience fields save on blur, not per keystroke. */
  const [audienceDraft, setAudienceDraft] = useState<AudienceProfile>({
    who: "",
    where: "",
    context: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        get<Settings>("/ai/settings"),
        get<Usage>("/ai/usage"),
      ]);
      setSettings(s);
      setUsage(u);
      setCapInput(s.monthlyCapUsd === null ? "" : String(s.monthlyCapUsd));
      setAudienceDraft(s.audience);
      const hitCap =
        s.monthlyCapUsd !== null && u.month >= s.monthlyCapUsd;
      setDetailsOpen(hitCap);
    } catch (err) {
      toast({
        title: "Couldn't load AI settings",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * `audience` is partial on purpose: the form saves one field at a time, and
   * the server merges rather than replaces, so an untouched field is never
   * cleared by a neighbour's save.
   */
  const patch = async (
    changes: Partial<Omit<Settings, "audience">> & {
      audience?: Partial<AudienceProfile>;
    },
  ) => {
    if (!settings) return;
    const previous = settings;
    setSettings({
      ...settings,
      ...changes,
      audience: { ...settings.audience, ...(changes.audience || {}) },
    });
    setSaving(true);
    try {
      const next = await api<Settings>("/ai/settings", {
        method: "PATCH",
        body: changes,
      });
      setSettings(next);
      setAudienceDraft(next.audience);
    } catch (err) {
      setSettings(previous);
      setAudienceDraft(previous.audience);
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  /** Only writes when the value actually moved — blur fires on every tab out. */
  const commitAudience = (key: keyof AudienceProfile) => {
    const value = audienceDraft[key].trim().slice(0, AUDIENCE_MAX);
    if (!settings || value === settings.audience[key]) return;
    void patch({ audience: { [key]: value } });
  };

  const commitCap = () => {
    const raw = capInput.trim();
    if (raw === "") return void patch({ monthlyCapUsd: null });
    const cap = Number(raw);
    if (!Number.isFinite(cap) || cap < 0) {
      toast({
        title: "Invalid limit",
        description: "Enter a positive amount, or leave blank for no limit.",
        variant: "destructive",
      });
      setCapInput(
        settings?.monthlyCapUsd === null
          ? ""
          : String(settings?.monthlyCapUsd ?? ""),
      );
      return;
    }
    void patch({ monthlyCapUsd: Math.round(cap * 100) / 100 });
  };

  if (loading || !settings || !usage) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const capped =
    settings.monthlyCapUsd !== null && usage.month >= settings.monthlyCapUsd;
  const pct =
    settings.monthlyCapUsd && settings.monthlyCapUsd > 0
      ? Math.min(100, (usage.month / settings.monthlyCapUsd) * 100)
      : 0;

  const hasDetails =
    usage.byFeature.length > 0 || usage.recent.length > 0;

  return (
    <div className="space-y-8">
      <SettingsSection
        title="Controls"
        description="Turn features on or off and set a monthly spend limit."
      >
        <div className="space-y-1 overflow-hidden rounded-xl border border-border divide-y divide-border">
          <div className="flex items-start justify-between gap-4 px-4 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="ai-enabled">AI features</Label>
              <p className="text-xs text-muted-foreground">
                Suggestions and research. Nothing is sent while this is off.
              </p>
            </div>
            <Switch
              id="ai-enabled"
              checked={settings.enabled}
              disabled={saving}
              onCheckedChange={(v) => void patch({ enabled: v })}
            />
          </div>

          <div className="flex items-start justify-between gap-4 px-4 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="ai-search" className="flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5" />
                Web search for research
              </Label>
              <p className="text-xs text-muted-foreground">
                Checks claims against sources — slower and more expensive.
              </p>
            </div>
            <Switch
              id="ai-search"
              checked={settings.webSearchEnabled}
              disabled={saving || !settings.enabled}
              onCheckedChange={(v) => void patch({ webSearchEnabled: v })}
            />
          </div>

          <div className="space-y-1.5 px-4 py-3">
            <Label htmlFor="ai-cap">Monthly limit</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                id="ai-cap"
                inputMode="decimal"
                placeholder="No limit"
                className="max-w-[10rem]"
                value={capInput}
                disabled={saving}
                onChange={(e) => setCapInput(e.target.value)}
                onBlur={commitCap}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Features pause at this amount. Leave blank for no limit.
            </p>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Your congregation"
        description="Who you're writing for. Questions are asked with these people in mind — what will land, what needs explaining, where a claim meets resistance."
      >
        <div className="space-y-4 rounded-xl border border-border p-4">
          {AUDIENCE_FIELDS.map(({ key, label, placeholder, hint }) => (
            <div key={key} className="space-y-1.5">
              <Label htmlFor={`audience-${key}`}>{label}</Label>
              <Textarea
                id={`audience-${key}`}
                rows={3}
                maxLength={AUDIENCE_MAX}
                placeholder={placeholder}
                value={audienceDraft[key]}
                disabled={saving}
                onChange={(e) =>
                  setAudienceDraft((prev) => ({ ...prev, [key]: e.target.value }))
                }
                onBlur={() => commitAudience(key)}
              />
              <p className="text-xs text-muted-foreground">{hint}</p>
            </div>
          ))}
        </div>

        <div className="space-y-1.5 text-xs text-muted-foreground">
          <p>
            This steers the questions rather than dictating them — it won't
            soften a hard question because the room might not like it, and it
            never overrides what your draft actually says.
          </p>
          <p>
            Preaching somewhere else? Set that document's own{" "}
            <span className="font-medium text-foreground">Audience</span> field
            and it's used instead of this, for that document only.
          </p>
          <p>Leave it all blank and nothing about an audience is sent at all.</p>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Spend"
        description="What AI has cost on this account (excludes separate web-search fees)."
      >
        <div className="grid grid-cols-3 gap-2">
          {[
            ["Today", usage.today],
            ["This month", usage.month],
            ["All time", usage.allTime],
          ].map(([label, value]) => (
            <div
              key={label as string}
              className="rounded-xl border border-border p-3"
            >
              <p className="text-xs text-muted-foreground">{label as string}</p>
              <p className="text-lg font-semibold tabular-nums">
                {money(value as number)}
              </p>
            </div>
          ))}
        </div>

        {settings.monthlyCapUsd !== null && (
          <div className="space-y-1.5">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full transition-all ${capped ? "bg-destructive" : "bg-primary"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {money(usage.month)} of {money(settings.monthlyCapUsd)} this month
            </p>
          </div>
        )}

        {capped && (
          <div className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">
              Monthly limit reached — AI is paused until you raise it or the
              month rolls over.
            </p>
          </div>
        )}

        {hasDetails && (
          <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-muted-foreground"
              >
                Usage details
                <ChevronDown
                  className={cn(
                    "ml-1 h-3.5 w-3.5 transition-transform",
                    detailsOpen && "rotate-180",
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-2">
              {usage.byFeature.length > 0 && (
                <div className="space-y-1">
                  {usage.byFeature.map((f) => (
                    <div
                      key={f.feature}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="text-muted-foreground">
                        {FEATURE_LABELS[f.feature] || f.feature}
                        <span className="ml-1.5 opacity-60">×{f.calls}</span>
                      </span>
                      <span className="tabular-nums">{money(f.costUsd)}</span>
                    </div>
                  ))}
                </div>
              )}

              {usage.recent.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Recent calls
                  </p>
                  <div className="space-y-1">
                    {usage.recent.slice(0, 10).map((r, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="truncate text-muted-foreground">
                          {FEATURE_LABELS[r.feature] || r.feature}
                          {r.searchCount > 0 && (
                            <Globe className="ml-1.5 inline h-3 w-3" />
                          )}
                          <span className="ml-1.5 opacity-60">
                            {new Date(r.createdAt).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </span>
                        </span>
                        <span className="tabular-nums">
                          {money(r.costUsd)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => void load()}
                  >
                    Refresh
                  </Button>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </SettingsSection>
    </div>
  );
}
